// @flow

import {Tag} from 'docs/util';
import type {MarkdocFragmentRegistration} from 'docs/markdoc';
import {KeyToken} from './KeyToken.markdoc';

const TAB = '  ';
const LINE_SEPARATOR = ` \\\n`;

const isString = (str) => typeof str === 'string' || str instanceof String;

// param can be a Ast.Variable so we check it is a string
const escapeQuotes = (param) =>
  isString(param) ? param.replace(/"/g, '\\"') : param;

const stringifyParam = (param) => {
  // In the Markdoc render function we can only return
  // strings or AST elements, so stringify numbers.
  if (!isNaN(param)) {
    return [String(param)];
  }

  return [`"`, escapeQuotes(param), `"`];
};

// Convert 2D Array of parameters into a flat list with
// quotes (if needed) and add neccesary tokens needed for
// the curl command.
const stringifyCurlParams = (params) => {
  // We cannot do string interpolation because the parameters could be Markdoc
  // AST Nodes, so we return an array.
  const options = params.flatMap((currentLine) => {
    if (currentLine.length === 2) {
      const [flag, v] = currentLine;
      const value = stringifyParam(v);

      return [TAB, flag, ' ', ...value, LINE_SEPARATOR];
    } else if (currentLine.length === 3) {
      const [flag, k, v] = currentLine;

      const key = stringifyParam(k);
      const value = stringifyParam(v);

      return [TAB, flag, ' ', ...key, '=', ...value, LINE_SEPARATOR];
    } else {
      throw new Error(`Invalid number of arguments ${currentLine}`);
    }
  });

  if (options.length > 0) {
    // Remove the extra line separator
    options.pop();
  }

  return options;
};

export const Curl: MarkdocFragmentRegistration = {
  tag: 'curl',
  description: 'curl command',
  localize: false,
  selfClosing: true,
  attributes: {
    path: {
      type: String,
      description: 'Path to curl',
    },
    params: {
      type: Array,
      description: 'Parameters to pass into curl',
      default: [],
    },
    base_domain: {
      type: String,
      default: 'api.stripe.com',
      required: false,
    },
  },
  render(node, config) {
    const variables = (config.variables: any);
    const keys = variables.keys;
    const key = new Tag(KeyToken.component, {
      type: 'secret',
      value: keys.secret,
    });
    const attributes = node.renderAttributes(this.attributes, config);
    const path: string = (attributes.path: any);
    const params: Array<any> = (attributes.params: any);
    const baseDomain: string = (attributes.base_domain: any);
    const curlArguments = stringifyCurlParams(params);

    const command = `curl https://${baseDomain}`;

    // Note: In ERB the curl helper showed `pay curl` depending on whether it was
    // a devbox or not. We decided to remove this feature.

    const firstLine = [command, path, LINE_SEPARATOR];
    const secondLine = ['  -u ', key, ':'];

    // If there are no arguments, do not show a line separator
    const restOfLines =
      curlArguments.length > 0 ? [LINE_SEPARATOR, ...curlArguments] : [];

    return [...firstLine, ...secondLine, ...restOfLines];
  },
};
